import { DateTime } from 'luxon';
import React, { useMemo, useRef, useState } from 'react';
import { useUpdate } from 'react-use';

import type { Account } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import type { InvestmentTransactionTxnType } from 'app-components/forms/investment/investmentTransactionForm';
import {
  getInvestmentTransactionDisplayType,
  VIRTUAL_CASH_FLOW_TXN_TYPE,
} from 'app-components/forms/investment/investmentTransactionFormDisplay';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AttachmentsField from 'components/AttachmentsField';
import Dump from 'components/Dump';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import InvestmentTransactionTypeField from 'components/InvestmentTransactionTypeField';
import AccountField from 'components/QuickenControls/AccountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import QTip from 'components/QuickenControls/QTip';
import { useAppSelector } from 'hooks/redux';

import { investmentTransactionEditFormHooks } from 'components/Forms/InvestmentTransactionEditForm/investmentTransactionEditFormHooks';
import { tooltipText } from './labels';
import { TypeSpecificFields } from './TypeSpecificFields';
import type { DropzoneRef } from './types';
import { TypedSDatePicker } from './utils';

type InvestmentTransactionEditFormProps = Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  transaction?: Partial<TransactionProps>;
};

export default function InvestmentTransactionEditForm({
  transaction,
  ...otherProps
}: InvestmentTransactionEditFormProps): React.JSX.Element {
  const manager = investmentTransactionEditFormHooks.useManager();
  const accountId = investmentTransactionEditFormHooks.useField('accountId');
  const postedOn = investmentTransactionEditFormHooks.useField('postedOn');
  const investmentTxnTypeField = investmentTransactionEditFormHooks.useField('investmentTxnType');
  const isExcludedFromF2S = investmentTransactionEditFormHooks.useField('isExcludedFromF2S');
  const isExcludedFromReports = investmentTransactionEditFormHooks.useField('isExcludedFromReports');
  const memo = investmentTransactionEditFormHooks.useField('memo');
  const attachments = investmentTransactionEditFormHooks.useField('attachments');
  const checkNumber = investmentTransactionEditFormHooks.useField('checkNumber');
  const coa = investmentTransactionEditFormHooks.useField('coa');
  const [postedOnTouched, setPostedOnTouched] = useState(false);
  const [dateInfoText, setDateInfoText] = useState('');
  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const futureTxnsFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'futureTxns'));
  const openAccounts = useAppSelector((state) =>
    accountsSelectors.getOpenOrSelectedInvestmentAccounts(state, accountId.value ?? ''),
  );
  const investmentAccounts = useAppSelector(accountsSelectors.getInvestmentAccounts);
  const showCheckNumberField = useAppSelector((state) =>
    accountsSelectors.shouldAccountDisplayCheckNumberField(state, accountId.value),
  );
  const appearsOnYourStatement = useMemo(
    () =>
      `Appears as "${transaction?.cpData?.payee}" on ${DateTime.fromISO(
        transaction?.cpData?.postedOn || transaction?.postedOn || '',
      ).toLocaleString(DateTime.DATE_MED)}.`,
    [transaction],
  );
  const investmentTxnType = getInvestmentTransactionDisplayType({
    accountIsInvestment: Boolean(accountId.value && investmentAccounts.has(accountId.value)),
    investmentTxnType: investmentTxnTypeField.value,
    transactionType: manager.runSelector(({ state }) => state.type),
  });
  const ignoreIsDisabled = investmentTxnType !== VIRTUAL_CASH_FLOW_TXN_TYPE;
  const dropzoneRef = useRef<DropzoneRef>(null);
  const update = useUpdate();
  const {
    onClick: _onClick,
    onFocus: _onFocus,
    onBlur: _onBlur,
    onKeyDown: _onKeyDown,
    tabIndex: _tabIndex,
    ...dropzoneRootProps
  } = dropzoneRef.current?.getRootProps?.() || {};

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void manager.submit(undefined);
      }}
      {...dropzoneRootProps}
      {...otherProps}
    >
      <Box display='flex' gap={3} flexDirection='column' position='relative'>
        <Dump obj={{ state: manager.runSelector(({ state }) => state), transaction }} />
        {transaction?.cpData?.payee ? (
          <QTip wrapOnly>
            <Typography variant='captionRegular' color='textSecondary' noWrap display='block'>
              {appearsOnYourStatement}
            </Typography>
          </QTip>
        ) : null}
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <AccountField
            accounts={openAccounts}
            doNotIncludeClosed
            initialAccountId={accountId.value}
            onSelected={(item: Account) => accountId.onValueChange(item?.id)}
            label='Account'
            readOnly={transaction?.source !== 'USER_ENTERED'}
            id='account-select'
            sx={{ width: 250 }}
          />
          <TypedSDatePicker
            id='posted-on'
            label='Date'
            maxDate={futureTxnsFf ? DateTime.now().plus({ year: 1 }) : undefined}
            value={postedOn.value ? DateTime.fromISO(postedOn.value) : null}
            onChange={(date: DateTime | null, value: string) => {
              postedOn.onValueChange(date?.isValid ? date.toISODate() || '' : value);
              setPostedOnTouched(true);
              setDateInfoText(
                futureTxnsFf && date?.isValid && date > currentDate ? 'Future dates will create a Reminder.' : '',
              );
            }}
            textFieldProps={{
              error: Boolean(postedOnTouched && postedOn.error),
              helperText: (postedOnTouched && (postedOn.helperText || dateInfoText)) || '',
            }}
          />
        </Box>
        <Box display='flex' alignItems='center' gap={2}>
          <InvestmentTransactionTypeField
            id='investmentTxnType'
            sx={{ minWidth: 250 }}
            value={investmentTxnType}
            onChange={(value: InvestmentTransactionTxnType) => investmentTxnTypeField.onValueChange(value)}
            textFieldProps={{
              error: investmentTxnTypeField.error,
              helperText: investmentTxnTypeField.helperText,
            }}
          />
          <InfoTooltipIcon tooltipText={tooltipText(investmentTxnType)} />
          {transactionsUtils.isCategoryVisibleOnInvestmentTxnType(investmentTxnType) ? (
            <CategoryField
              id='category'
              editable
              disableUnderline
              textFieldVariant='filled'
              allowBlank
              createEnabled
              label='Category'
              width={240}
              value={coa.value}
              onChange={(nextCoa: TransactionProps['coa']) => coa.onValueChange(nextCoa)}
            />
          ) : null}
        </Box>
        <TypeSpecificFields investmentTxnType={investmentTxnType} />
        <Divider />
        <Box display='flex'>
          <Box>
            <Typography variant='captionRegular' color='text.secondary'>
              Exclusions
            </Typography>
            <Box>
              <FormControlLabel
                disabled={ignoreIsDisabled}
                checked={Boolean(isExcludedFromF2S.value)}
                value={Boolean(isExcludedFromF2S.value)}
                onChange={(_event: React.SyntheticEvent, newChecked: boolean) =>
                  isExcludedFromF2S.onValueChange(newChecked)
                }
                control={<Checkbox />}
                label='Exclude from Spending Plan'
                labelPlacement='end'
              />
              <FormControlLabel
                disabled={ignoreIsDisabled}
                checked={Boolean(isExcludedFromReports.value)}
                value={Boolean(isExcludedFromReports.value)}
                onChange={(_event: React.SyntheticEvent, newChecked: boolean) =>
                  isExcludedFromReports.onValueChange(newChecked)
                }
                control={<Checkbox />}
                label='Exclude from Reports'
                labelPlacement='end'
              />
            </Box>
          </Box>
          <Box display='flex' flexDirection='column' gap={1}>
            <Typography variant='captionRegular' color='text.secondary'>
              Additional info
            </Typography>
            <TextField
              id='memo'
              label='Note'
              sx={{ width: 280 }}
              value={memo.value || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => memo.onValueChange(event.target.value || '')}
            />
            <AttachmentsField
              id='attachments'
              attachments={attachments.value}
              onChange={(_event: React.SyntheticEvent, nextAttachments: TransactionProps['attachments']) =>
                attachments.onValueChange(nextAttachments)
              }
              showDropZone
              onDropzoneChanged={update}
              dropzoneRef={dropzoneRef}
            />
            {showCheckNumberField && transactionsUtils.isCheckNumberVisibleOnInvestmentTxnType(investmentTxnType) ? (
              <TextField
                id='checkNumber'
                label='Check #'
                value={checkNumber.value || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  checkNumber.onValueChange(event.target.value || '')
                }
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </form>
  );
}
